/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'

import IdentifiersForm from 'modules/payroll_employees/lib/forms/identifiersForm'
import identifiers from 'modules/payroll_employees/store/collections/identifiers'
import type Identifier from 'modules/payroll_employees/store/models/Identifier'

export enum IdentifierFieldName {
  SocialSecurityNumber = 'social_security_number',
  TaxId = 'tax_id',
  EmployeeId = 'employee_id',
}

interface Props {
  employeeId: number
  country: string
  setSubmitEventHandler: (eventHandler: () => void) => void
}

const useIdentifierForm = ({ employeeId, country, setSubmitEventHandler }: Props) => {
  const [form, setForm] = React.useState(
    new IdentifiersForm({ country: country, employee_id: employeeId })
  )

  const [identifier, setIdentifier] = React.useState<Identifier | null>(null)

  useEffect(() => {
    const action = async () => {
      const response = await asyncAction(
        () =>
          identifiers.fetch({
            data: {
              employees_ids: [employeeId],
              country: country,
            },
          }),
        { successFlash: false }
      )

      if (isSuccessful(response)) {
        const employeeIdentifier = identifiers.findByEmployee(employeeId)
        if (employeeIdentifier) {
          setIdentifier(employeeIdentifier)
        }

        setForm(
          new IdentifiersForm({
            social_security_number: employeeIdentifier?.socialSecurityNumber ?? '',
            tax_id: employeeIdentifier?.taxId ?? '',
            country: country,
            employee_id: employeeId,
          })
        )
      }
    }
    action()
  }, [employeeId])

  useEffect(() => {
    setSubmitEventHandler(async () => {
      const { success } = await asyncAction(async () => {
        if (identifier && identifier.employeeId === employeeId) {
          return form.save(identifier)
        } else {
          const response = await form.create(identifiers)
          setIdentifier?.(identifiers.findByEmployee(employeeId)!)
          return response
        }
      })
      if (success) {
        form.cleanAll()
        form.resetErrors()
      }
    })
  }, [
    identifier?.socialSecurityNumber,
    identifier?.taxId,
    form.get(IdentifierFieldName.SocialSecurityNumber).value,
    form.get(IdentifierFieldName.TaxId).value,
  ])

  return form
}

export default useIdentifierForm
